'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { notificationService, type AppNotification } from '@/lib/services/notificationService';

export function useNotifications(email: string | null | undefined) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    const data = await notificationService.getForUser(email);
    setNotifications(data);
    setLoading(false);
  }, [email]);

  useEffect(() => {
    if (!email) {
      setNotifications([]);
      return;
    }

    load();

    // Subscribe to real-time inserts for this visitor's email
    const supabase = createClient();
    const channel = supabase
      .channel(`notifications:${email}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `visitor_email=eq.${email}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as AppNotification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `visitor_email=eq.${email}`,
        },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === (payload.new as AppNotification).id ? (payload.new as AppNotification) : n))
          );
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [email, load]);

  const markRead = useCallback(
    async (id: string) => {
      await notificationService.markRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    },
    []
  );

  const markAllRead = useCallback(async () => {
    if (!email) return;
    await notificationService.markAllRead(email);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }, [email]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return { notifications, loading, unreadCount, markRead, markAllRead, reload: load };
}
